import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import AndroidAuthortityLogo from './assets/android-authority-logo.png';
import AppsWeLoveLogo from './assets/apps-we-love-logo.png';
import GoldenKittyLogo from './assets/golden-kitty-logo.png';
import LifehackerLogo from './assets/lifehacker-logo.png';
import LifeWireLogo from './assets/lifewire-logo.png';
import MensHealthLogo from './assets/mens-health-logo.png';
import MensJournalLogo from './assets/mens-journal-logo.png';
import ProductHuntLogo from './assets/product-hunt-logo.png';
import SportsIllustratedLogo from './assets/sports-illustrated-logo.png';

// --- Styled Components ---

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
`;

const SliderTrack = styled.div<{ $offset: number; $isTransitioning: boolean; $duration: number }>`
  display: flex;
  /* The calculation: offset determines how many slides we've moved */
  transform: translateX(-${props => props.$offset}%);
  transition: ${props =>
    props.$isTransitioning ? `transform ${props.$duration}ms ease-in-out` : 'none'};
`;

const Slide = styled.div`
  /* Default: 1 slide visible (Mobile) */
  flex: 0 0 100%;
  padding: 0 10px;
  box-sizing: border-box;

  /* Tablet: 2 slides visible */
  @media (min-width: 768px) {
    flex: 0 0 50%;
  }

  /* Desktop: 3 slides visible */
  @media (min-width: 1024px) {
    flex: 0 0 33.333%;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 90px;
  width: 100%;
`;

// --- Component Logic ---
const featuredLogos = [
  { src: AndroidAuthortityLogo, alt: 'android-authority' },
  { src: AppsWeLoveLogo, alt: 'apps-we-love-logo' },
  { src: GoldenKittyLogo, alt: 'golden-kitty-logo' },
  { src: LifehackerLogo, alt: 'lifehacker-logo' },
  { src: LifeWireLogo, alt: 'lifewire-logo' },
  { src: MensHealthLogo, alt: 'mens-health-logo' },
  { src: MensJournalLogo, alt: 'mens-journal-logo' },
  { src: ProductHuntLogo, alt: 'product-hunt-logo' },
  { src: SportsIllustratedLogo, alt: 'sports-illustrated-logo' },
];

export default function ElementorStyledCarousel() {
  const [items, setItems] = useState(featuredLogos);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [offset, setOffset] = useState(0);

  const SPEED = 3000; // Time between slides
  const DURATION = 800; // Animation length

  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, SPEED);

    return () => clearInterval(interval);
  }, [items, isTransitioning]);

  const handleNext = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    // Move the track forward by one slide's width
    // We use a responsive calculation for the offset here
    const slideWidth =
      typeof window !== 'undefined' && window.innerWidth >= 1024
        ? 33.333
        : window.innerWidth >= 768
        ? 50
        : 100;
    setOffset(slideWidth);

    setTimeout(() => {
      // 1. Physically move the first item to the end of the array
      const newItems = [...items];
      const firstItem = newItems.shift();
      if (firstItem) newItems.push(firstItem);

      // 2. Turn off transitions and reset offset to 0 instantly
      // This is the "magic" that makes it look infinite
      setIsTransitioning(false);
      setItems(newItems);
      setOffset(0);
    }, DURATION);
  };

  return (
    <CarouselContainer>
      <SliderTrack $offset={offset} $isTransitioning={isTransitioning} $duration={DURATION}>
        {items.map((logo, idx) => (
          <Slide key={idx}>
            <ImageWrapper>
              <Image
                src={logo.src}
                alt={logo.alt}
                fill
                style={{ objectFit: 'contain' }}
                sizes="100%"
              />
            </ImageWrapper>
          </Slide>
        ))}
      </SliderTrack>
    </CarouselContainer>
  );
}
